import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  useGetSubmissionTimelineQuery,
  useAddSubmissionCommentMutation,
} from '@/redux/collaborationsApi'
import { parseSubmissionData } from '@adthlete/common/utils/submissionDataParser'

interface TimelineEvent {
  type: 'submission' | 'comment'
  timestamp: string
  actor: {
    id: number
    name: string
    avatar?: string
  }
  data: any
  commentType?: string
}

interface SubmissionTimelineProps {
  submissionId: number
  milestoneId: number
  currentUserRole?: string // 'athlete' | 'brand'
}

export const SubmissionTimeline: React.FC<SubmissionTimelineProps> = ({
  submissionId,
  milestoneId,
  currentUserRole = 'brand',
}) => {
  const { id: campaignId } = useParams<{ id: string }>()

  const { data: timelineData, isLoading: loading } = useGetSubmissionTimelineQuery(
    { campaignId: campaignId!, milestoneId, submissionId },
    { skip: !campaignId },
  )

  const [addSubmissionComment] = useAddSubmissionCommentMutation()

  const [commentText, setCommentText] = useState('')
  const [commentType, setCommentType] = useState('general')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim() || !campaignId) return

    setSubmitting(true)
    try {
      await addSubmissionComment({
        campaignId,
        milestoneId,
        submissionId,
        text: commentText,
        commentType,
      }).unwrap()

      setCommentText('')
      setCommentType('general')
    } catch (error) {
      console.error('Failed to add comment:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getCommentTypeColor = (type: string) => {
    switch (type) {
      case 'approval':
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      case 'revision_request':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      case 'question':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }

  const getCommentTypeLabel = (type: string) => {
    switch (type) {
      case 'approval':
        return 'Approved'
      case 'revision_request':
        return 'Revision Requested'
      case 'question':
        return 'Question'
      case 'reply':
        return 'Reply'
      default:
        return 'Comment'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-white">Loading timeline...</div>
      </div>
    )
  }

  if (!timelineData) {
    console.log('No timeline data, showing message')
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-white">No timeline data available</div>
      </div>
    )
  }

  console.log('Rendering timeline with', timelineData.timeline?.length, 'events')

  return (
    <div className="space-y-6">
      {/* Timeline Events */}
      <div className="space-y-4">
        {timelineData.timeline?.map((event: TimelineEvent, index: number) => (
          <div key={index} className="flex gap-3">
            {/* Avatar — hidden for athlete role to save horizontal space on mobile */}
            {currentUserRole !== 'athlete' && (
              <div className="shrink-0">
                {event.actor.avatar ? (
                  <img
                    src={event.actor.avatar}
                    alt={event.actor.name}
                    className="h-10 w-10 rounded-full"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white font-semibold border border-white/20">
                    {event.actor.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            )}

            {/* Event Content */}
            <div className="flex-1 min-w-0">
              <div className="bg-white/5 rounded-lg border border-white/10 p-4">
                {/* Header */}
                <div className="flex flex-col gap-1 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{event.actor.name}</span>
                    {event.type === 'submission' && (
                      <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">
                        Submitted
                      </span>
                    )}
                    {event.type === 'comment' && event.commentType && (
                      <span
                        className={`text-xs px-2 py-1 rounded border ${getCommentTypeColor(event.commentType)}`}
                      >
                        {getCommentTypeLabel(event.commentType)}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{formatDate(event.timestamp)}</span>
                </div>

                {/* Content */}
                {event.type === 'submission' && (
                  <div className="text-gray-300">
                    <p className="font-medium mb-2">Creative Concept Submitted</p>
                    {event.data.description &&
                      (() => {
                        const parsed = parseSubmissionData(event.data.description)
                        if (parsed.fields.length > 0) {
                          return (
                            <div className="text-sm space-y-3 mb-2">
                              {parsed.fields.map((field, idx) => (
                                <div key={idx}>
                                  <p className="text-gray-400 font-medium mb-1">{field.label}:</p>
                                  <p className="text-gray-300 whitespace-pre-wrap">
                                    {field.value}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )
                        }
                        return null
                      })()}
                    {event.data.version && (
                      <p className="text-xs text-gray-500">Version {event.data.version}</p>
                    )}
                  </div>
                )}

                {event.type === 'comment' && (
                  <div>
                    <p className="text-gray-300">{event.data.text}</p>

                    {/* Nested Replies */}
                    {event.data.replies && event.data.replies.length > 0 && (
                      <div className="mt-3 ml-4 space-y-2 border-l-2 border-white/20 pl-4">
                        {event.data.replies.map((reply: any, replyIndex: number) => (
                          <div key={replyIndex} className="text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-white">{reply.actor.name}</span>
                              <span className="text-xs text-gray-500">
                                {formatDate(reply.timestamp)}
                              </span>
                            </div>
                            <p className="text-gray-400">{reply.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Comment Form (only for brand users) */}
      {currentUserRole === 'brand' && (
        <div className="bg-white/5 rounded-lg border border-white/10 p-4">
          <form onSubmit={handleSubmitComment}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-white mb-2">Add Comment</label>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Enter your feedback..."
                className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                disabled={submitting}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-white mb-2">Comment Type</label>
                <select
                  value={commentType}
                  onChange={(e) => setCommentType(e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={submitting}
                >
                  <option value="general">General Comment</option>
                  <option value="question">Question</option>
                  <option value="revision_request">Request Revision</option>
                  <option value="approval">Approve Submission</option>
                </select>
              </div>

              <div className="pt-7">
                <button
                  type="submit"
                  disabled={!commentText.trim() || submitting}
                  className={`px-6 py-2 rounded-lg font-medium ${
                    commentType === 'approval'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : commentType === 'revision_request'
                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                  } disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors`}
                >
                  {submitting
                    ? 'Submitting...'
                    : commentType === 'approval'
                      ? 'Approve'
                      : commentType === 'revision_request'
                        ? 'Request Revision'
                        : 'Add Comment'}
                </button>
              </div>
            </div>

            {commentType === 'approval' && (
              <p className="mt-2 text-sm text-green-600">
                ✓ This will mark the submission as approved and complete the milestone.
              </p>
            )}
            {commentType === 'revision_request' && (
              <p className="mt-2 text-sm text-yellow-600">
                ⚠ This will request revisions from the athlete.
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  )
}
